import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, EmptyState, PageHeader } from "@pericles/framework-frontend";
import { EXECUTION_STATUS, PRISMA_EXECUTION_STATUS, type ExecutionStatus } from "@pericles/shared";

import {
  analyzeMeetingAudioSession,
  createMeetingAudioSession,
  createProjectMeeting,
  deleteProjectMeeting,
  fetchLatestMeetingAudioAnalysis,
  finalizeMeetingAudioSession,
  getMeetingAudioSession,
  fetchProjectMeeting,
  fetchProjectMeetings,
  generateMeetingQuestions,
  generateMeetingArtifacts,
  fetchMeetingArtifacts,
  type LatestMeetingAudioAnalysisResult,
  uploadMeetingAudioChunk,
  updateProjectMeeting,
  updateProjectMeetingQuestions,
} from "../api/projectMeetingsClient";
import type {
  GeneratedMeetingQuestion,
  MeetingAudioSession,
  MeetingGeneratedArtifacts,
  ProjectMeeting,
  ProjectMeetingQuestion,
  ProjectMeetingStatus,
} from "../types/meetings";
import { EXECUTION_STATUS_UI, normalizeExecutionStatus } from "../domain/statusAdapters";

import { InlineAlert } from "./InlineAlert";
import { MeetingCoachPanel } from "./MeetingCoachPanel";
import {
  ReviewGeneratedMeetingQuestionsModal,
  type ReviewedMeetingQuestion,
} from "./ReviewGeneratedMeetingQuestionsModal";
import {
  Button,
  Input,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
} from "./ui";

interface ProjectMeetingsTabProps {
  projectId: string;
  showHeader?: boolean;
}

const STATUS_BADGES: Record<ProjectMeetingStatus, { label: string; variant: React.ComponentProps<typeof Badge>["variant"] }> =
  EXECUTION_STATUS_UI;

const RECOMMENDED_TEMPLATES: { key: string; label: string }[] = [
  { key: "discovery", label: "Discovery" },
  { key: "risks", label: "Risk review" },
  { key: "delivery", label: "Delivery planning" },
  { key: "retro", label: "Retrospective" },
];

function formatDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatQuestion(question: ReviewedMeetingQuestion): ProjectMeetingQuestion {
  return {
    question: question.question.trim(),
    category: question.category?.trim() || undefined,
    intent: question.intent?.trim() || undefined,
  } satisfies ProjectMeetingQuestion;
}

function renderStatusBadge(status: ProjectMeetingStatus): JSX.Element {
  const normalized = normalizeExecutionStatus(status) ?? PRISMA_EXECUTION_STATUS.planned;
  const config = STATUS_BADGES[normalized] ?? { label: normalized, variant: "default" };
  return <Badge label={config.label} variant={config.variant} />;
}

function getRecordedFileExtension(mimeType: string | null | undefined): string {
  const normalized = String(mimeType ?? "").toLowerCase();
  if (normalized.includes("webm")) return ".webm";
  if (normalized.includes("wav")) return ".wav";
  if (normalized.includes("mpeg") || normalized.includes("mp3")) return ".mp3";
  if (normalized.includes("mp4") || normalized.includes("m4a") || normalized.includes("x-m4a")) return ".m4a";
  if (normalized.includes("x-wav")) return ".wav";
  if (normalized.includes("ogg")) return ".ogg";
  return ".webm";
}

function normalizeAudioUploadFilename(name: string | undefined): string {
  const trimmed = String(name ?? "").trim().replace(/[\\/:*?"<>|]+/g, "_");
  if (!trimmed) return "";
  return trimmed;
}

function buildAudioUploadFilename(
  meetingId: string,
  sourceFile: { name?: string; type?: string | null },
  index: number,
): string {
  const normalizedName = normalizeAudioUploadFilename(sourceFile.name);
  if (normalizedName) {
    const hasDot = normalizedName.includes(".");
    if (hasDot) {
      return normalizedName;
    }
    return `${normalizedName}${getRecordedFileExtension(sourceFile.type ?? null)}`;
  }

  const safeMeetingId = meetingId.replace(/[^a-z0-9-]/gi, "-");
  return `meeting-${safeMeetingId}-${Date.now()}-${String(index + 1).padStart(2, "0")}${getRecordedFileExtension(
    sourceFile.type ?? null,
  )}`;
}

function getPreferredRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") {
    return undefined;
  }
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg", "audio/ogg"];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate));
}

function isIOSFamilyDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const userAgent = navigator.userAgent ?? "";
  const platform = navigator.platform ?? "";
  const maxTouchPoints = navigator.maxTouchPoints ?? 0;

  return /iPad|iPhone|iPod/i.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1);
}

function isStandaloneWebAppDisplayMode(): boolean {
  if (typeof window === "undefined") return false;
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  const isNavigatorStandalone = Boolean(navigatorWithStandalone.standalone);
  const isMediaStandalone =
    typeof window.matchMedia === "function" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches);

  return isNavigatorStandalone || isMediaStandalone;
}

const AUDIO_UPLOAD_ACCEPT = ".m4a,.wav,.mp3,.mp4,.mpeg,.mpga,.ogg,.webm,audio/*";

function getLatestTranscriptAnswer(meeting: ProjectMeeting | null): string {
  if (!meeting?.questionsAndAnswers?.length) {
    return "";
  }
  const reversed = [...meeting.questionsAndAnswers].reverse();
  const transcriptQuestion = reversed.find((item) => {
    const id = String(item.id ?? "");
    const category = String(item.category ?? "");
    const intent = String(item.intent ?? "");
    return (
      id.startsWith("auto-transcript-") ||
      category === "transcript_capture" ||
      intent === "captured_meeting_transcript"
    );
  });
  return transcriptQuestion?.answer?.trim() ?? "";
}

export function ProjectMeetingsTab({ projectId, showHeader = true }: ProjectMeetingsTabProps): JSX.Element {
  const [meetings, setMeetings] = useState<ProjectMeeting[]>([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState<string>("");
  const [formTemplate, setFormTemplate] = useState<string>(RECOMMENDED_TEMPLATES[0]?.key ?? "discovery");
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<GeneratedMeetingQuestion[]>([]);
  const [isReviewOpen, setIsReviewOpen] = useState<boolean>(false);
  const [statusUpdates, setStatusUpdates] = useState<Record<string, ProjectMeetingStatus>>({});
  const [editedQuestions, setEditedQuestions] = useState<ProjectMeetingQuestion[]>([]);
  const [saveAnswersLoading, setSaveAnswersLoading] = useState<boolean>(false);
  const [saveAnswersError, setSaveAnswersError] = useState<string | null>(null);
  const [artifactsByMeeting, setArtifactsByMeeting] = useState<Record<string, MeetingGeneratedArtifacts>>({});
  const [artifactsLoadingMeetingId, setArtifactsLoadingMeetingId] = useState<string | null>(null);
  const [artifactsError, setArtifactsError] = useState<string | null>(null);
  const [audioSession, setAudioSession] = useState<MeetingAudioSession | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [audioAssistantMessage, setAudioAssistantMessage] = useState<string | null>(null);
  const [audioTranscriptPreview, setAudioTranscriptPreview] = useState<string>("");
  const [audioAnalysisByMeeting, setAudioAnalysisByMeeting] = useState<
    Record<string, LatestMeetingAudioAnalysisResult | null>
  >({});
  const [audioAnalysisError, setAudioAnalysisError] = useState<string | null>(null);
  const [audioAnalysisLoadingMeetingId, setAudioAnalysisLoadingMeetingId] = useState<string | null>(null);
  const [isRecordingAudio, setIsRecordingAudio] = useState<boolean>(false);
  const [recordingStartedAtMs, setRecordingStartedAtMs] = useState<number | null>(null);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState<number>(0);
  const [isAudioBusy, setIsAudioBusy] = useState<boolean>(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioUploadInputRef = useRef<HTMLInputElement | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioSessionRef = useRef<MeetingAudioSession | null>(null);
  const isRecordingAudioRef = useRef<boolean>(false);
  const audioChunkSequenceRef = useRef<number>(0);
  const audioChunkUploadQueueRef = useRef<Promise<void>>(Promise.resolve());
  const audioChunkUploadErrorRef = useRef<Error | null>(null);
  const coachRefreshInFlightRef = useRef<boolean>(false);
  const audioAssistantTimingsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const normalizeMeetingStatus = useCallback(
    (status?: ProjectMeetingStatus): ProjectMeetingStatus =>
      normalizeExecutionStatus(status) ?? PRISMA_EXECUTION_STATUS.planned,
    [],
  );

  useEffect(() => {
    let cancelled = false;

    const loadMeetings = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await fetchProjectMeetings(projectId);
        if (!cancelled) {
          const normalized = result.map((meeting) => ({
            ...meeting,
            status: normalizeMeetingStatus(meeting.status),
          }));
          setMeetings(normalized);
          setSelectedMeetingId((current) => current ?? normalized[0]?.id ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Failed to load meetings";
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadMeetings();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const selectedMeeting = useMemo(
    () => meetings.find((meeting) => meeting.id === selectedMeetingId) ?? null,
    [meetings, selectedMeetingId],
  );
  const selectedMeetingAudioAnalysis = useMemo(
    () => (selectedMeeting ? audioAnalysisByMeeting[selectedMeeting.id] ?? null : null),
    [audioAnalysisByMeeting, selectedMeeting],
  );
  const isIOSDevice = useMemo(() => isIOSFamilyDevice(), []);
  const isStandaloneWebApp = useMemo(() => isStandaloneWebAppDisplayMode(), []);
  const shouldPreferNativeRecorder = isIOSDevice || isStandaloneWebApp;
  const shouldWarnAboutBackgroundCapture = isIOSDevice && isStandaloneWebApp;

  useEffect(() => {
    audioSessionRef.current = audioSession;
  }, [audioSession]);

  useEffect(() => {
    isRecordingAudioRef.current = isRecordingAudio;
  }, [isRecordingAudio]);

  const supportsLiveRecording =
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function";
  const audioRecoveryHint = useMemo(() => {
    const normalizedError = String(audioError ?? "").toLowerCase();
    if (
      normalizedError.includes("corrupted") ||
      normalizedError.includes("unsupported format") ||
      normalizedError.includes("unsupported media type") ||
      normalizedError.includes("audio file might be corrupted")
    ) {
      return "On iPhone or iPad, record with Voice Memos, then import the exported .m4a or .wav file here. Keep Forum open in the foreground during upload and transcription.";
    }
    if (shouldWarnAboutBackgroundCapture) {
      return "In iPhone PWA mode, browser microphone capture can pause when the screen locks or the app moves to the background. Native Voice Memos import is the safer path.";
    }
    return null;
  }, [audioError, shouldWarnAboutBackgroundCapture]);

  const releaseRecorderResources = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onerror = null;
    }
    mediaRecorderRef.current = null;

    const stream = mediaStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    mediaStreamRef.current = null;
  }, []);

  const clearAudioAssistantTimers = useCallback(() => {
    audioAssistantTimingsRef.current.forEach((timer) => {
      clearTimeout(timer);
    });
    audioAssistantTimingsRef.current = [];
  }, []);

  const setAudioAssistantStatus = useCallback(
    (message: string | null, clearAfterMs?: number) => {
      clearAudioAssistantTimers();
      setAudioAssistantMessage(message);
      if (!message) return;

      if (typeof clearAfterMs === "number" && clearAfterMs > 0) {
        const timer = setTimeout(() => {
          setAudioAssistantMessage((current) => (current === message ? null : current));
        }, clearAfterMs);
        audioAssistantTimingsRef.current.push(timer);
      }
    },
    [clearAudioAssistantTimers],
  );

  const keepAudioAssistantHelpful = useCallback((message: string) => {
    setAudioAssistantStatus(message);
    const timer = setTimeout(() => {
      setAudioAssistantStatus("Traitement en cours... cela peut prendre quelques instants.");
    }, 2500);
    audioAssistantTimingsRef.current.push(timer);
  }, [setAudioAssistantStatus]);

  const resetAudioChunkUploadState = useCallback(() => {
    audioChunkSequenceRef.current = 0;
    audioChunkUploadQueueRef.current = Promise.resolve();
    audioChunkUploadErrorRef.current = null;
  }, []);

  const waitForPendingAudioChunkUploads = useCallback(async (): Promise<void> => {
    await audioChunkUploadQueueRef.current;
    if (audioChunkUploadErrorRef.current) {
      throw audioChunkUploadErrorRef.current;
    }
  }, []);

  const enqueueRecordedAudioChunkUpload = useCallback(
    (sessionId: string, meetingId: string, chunk: Blob): Promise<void> => {
      if (!chunk || chunk.size === 0) {
        return Promise.resolve();
      }

      const sequence = audioChunkSequenceRef.current;
      audioChunkSequenceRef.current += 1;
      const filename = buildAudioUploadFilename(
        meetingId,
        {
          name: `meeting-${meetingId}-live-${String(sequence + 1).padStart(3, "0")}`,
          type: chunk.type || "audio/webm",
        },
        sequence,
      );

      const runUpload = async () => {
        if (audioChunkUploadErrorRef.current) {
          throw audioChunkUploadErrorRef.current;
        }

        const uploadedSession = await uploadMeetingAudioChunk(projectId, meetingId, sessionId, chunk, {
          sequence,
          filename,
        });
        audioSessionRef.current = uploadedSession;
        setAudioSession(uploadedSession);
      };

      const queued = audioChunkUploadQueueRef.current.catch(() => undefined).then(runUpload);
      audioChunkUploadQueueRef.current = queued
        .then(() => undefined)
        .catch((error: unknown) => {
          const resolvedError =
            error instanceof Error ? error : new Error("Failed to upload a live audio chunk");
          audioChunkUploadErrorRef.current = resolvedError;
          setAudioError(resolvedError.message);
          setAudioAssistantStatus(resolvedError.message);
        });

      return queued;
    },
    [projectId, setAudioAssistantStatus],
  );

  const refreshSelectedMeeting = useCallback(
    async (meetingId: string) => {
      const refreshed = await fetchProjectMeeting(projectId, meetingId);
      setMeetings((current) => current.map((item) => (item.id === refreshed.id ? refreshed : item)));
      setSelectedMeetingId(refreshed.id);
      setEditedQuestions(refreshed.questionsAndAnswers ?? []);
      setAudioTranscriptPreview(getLatestTranscriptAnswer(refreshed));
    },
    [projectId],
  );

  const loadArtifactsForMeeting = useCallback(
    async (meetingId: string, options: { updateLoadingState?: boolean } = {}) => {
      const { updateLoadingState = true } = options;

      if (!meetingId) return;

      if (updateLoadingState) {
        setArtifactsLoadingMeetingId(meetingId);
      }
      setArtifactsError(null);

      try {
        const artifacts = await fetchMeetingArtifacts(projectId, meetingId, { sourceType: "meeting_ai" });
        setArtifactsByMeeting((current) => ({ ...current, [meetingId]: artifacts }));
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load meeting drafts";
        setArtifactsError(message);
      } finally {
        if (updateLoadingState) {
          setArtifactsLoadingMeetingId((current) => (current === meetingId ? null : current));
        }
      }
    },
    [projectId],
  );

  const loadLatestMeetingAudioAnalysis = useCallback(
    async (meetingId: string, options: { updateLoadingState?: boolean } = {}) => {
      const { updateLoadingState = true } = options;

      if (!meetingId) return;

      if (updateLoadingState) {
        setAudioAnalysisLoadingMeetingId(meetingId);
      }
      setAudioAnalysisError(null);

      try {
        const result = await fetchLatestMeetingAudioAnalysis(projectId, meetingId);
        setAudioAnalysisByMeeting((current) => ({ ...current, [meetingId]: result }));
        if (result?.transcript && selectedMeetingId === meetingId) {
          setAudioTranscriptPreview((current) => {
            if (current.trim()) {
              return current;
            }
            return result.transcript.script?.trim() || result.transcript.text || "";
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load meeting coach";
        setAudioAnalysisError(message);
      } finally {
        if (updateLoadingState) {
          setAudioAnalysisLoadingMeetingId((current) => (current === meetingId ? null : current));
        }
      }
    },
    [projectId, selectedMeetingId],
  );

  const loadLiveMeetingAudioAnalysis = useCallback(
    async (
      meetingId: string,
      sessionId: string,
      options: { updateLoadingState?: boolean } = {},
    ): Promise<LatestMeetingAudioAnalysisResult> => {
      const { updateLoadingState = true } = options;

      if (updateLoadingState) {
        setAudioAnalysisLoadingMeetingId(meetingId);
      }
      setAudioAnalysisError(null);

      try {
        const result = await analyzeMeetingAudioSession(projectId, meetingId, sessionId, {
          transcription: {
            diarize: true,
          },
        });
        const currentSession = audioSessionRef.current;
        const shouldApplyResult =
          currentSession?.id === sessionId &&
          currentSession.meetingId === meetingId &&
          (isRecordingAudioRef.current ||
            currentSession.status === "recording" ||
            currentSession.status === "uploaded");

        if (shouldApplyResult) {
          setAudioAnalysisByMeeting((current) => ({ ...current, [meetingId]: result }));
        }
        if (shouldApplyResult && selectedMeetingId === meetingId) {
          setAudioTranscriptPreview(result.transcript.script?.trim() || result.transcript.text);
        }
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to analyze live meeting audio";
        setAudioAnalysisError(message);
        throw err;
      } finally {
        if (updateLoadingState) {
          setAudioAnalysisLoadingMeetingId((current) => (current === meetingId ? null : current));
        }
      }
    },
    [projectId, selectedMeetingId],
  );

  useEffect(() => {
    if (selectedMeeting) {
      setEditedQuestions(selectedMeeting.questionsAndAnswers ?? []);
      setAudioTranscriptPreview(getLatestTranscriptAnswer(selectedMeeting));
    } else {
      setEditedQuestions([]);
      setAudioTranscriptPreview("");
    }
    setSaveAnswersError(null);
    setAudioError(null);
    setAudioAnalysisError(null);
    setAudioAssistantStatus(null);
    clearAudioAssistantTimers();
    audioSessionRef.current = null;
    setAudioSession(null);
  }, [clearAudioAssistantTimers, selectedMeeting, setAudioAssistantStatus]);

  useEffect(() => {
    return () => {
      releaseRecorderResources();
      clearAudioAssistantTimers();
    };
  }, [clearAudioAssistantTimers, releaseRecorderResources]);

  useEffect(() => {
    releaseRecorderResources();
    clearAudioAssistantTimers();
    coachRefreshInFlightRef.current = false;
    setAudioAssistantStatus(null);
    setIsRecordingAudio(false);
    setRecordingStartedAtMs(null);
    setRecordingElapsedMs(0);
    audioChunksRef.current = [];
    resetAudioChunkUploadState();
  }, [clearAudioAssistantTimers, releaseRecorderResources, resetAudioChunkUploadState, selectedMeetingId, setAudioAssistantStatus]);

  useEffect(() => {
    if (!selectedMeetingId) return;

    void loadArtifactsForMeeting(selectedMeetingId);
    void loadLatestMeetingAudioAnalysis(selectedMeetingId);
  }, [loadArtifactsForMeeting, loadLatestMeetingAudioAnalysis, selectedMeetingId]);

  useEffect(() => {
    if (!isRecordingAudio || recordingStartedAtMs === null) {
      return;
    }

    setRecordingElapsedMs(Math.max(0, Date.now() - recordingStartedAtMs));
    const interval = setInterval(() => {
      setRecordingElapsedMs(Math.max(0, Date.now() - recordingStartedAtMs));
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isRecordingAudio, recordingStartedAtMs]);

  useEffect(() => {
    if (!selectedMeeting || !isRecordingAudio) {
      return;
    }

    const liveSession = audioSessionRef.current;
    if (!liveSession || liveSession.meetingId !== selectedMeeting.id) {
      return;
    }

    const interval = setInterval(() => {
      const currentSession = audioSessionRef.current;
      if (
        !currentSession ||
        currentSession.meetingId !== selectedMeeting.id ||
        currentSession.chunkCount <= 0 ||
        coachRefreshInFlightRef.current
      ) {
        return;
      }

      coachRefreshInFlightRef.current = true;
      void loadLiveMeetingAudioAnalysis(selectedMeeting.id, currentSession.id, {
        updateLoadingState: false,
      }).finally(() => {
        coachRefreshInFlightRef.current = false;
      });
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [isRecordingAudio, loadLiveMeetingAudioAnalysis, selectedMeeting]);

  const handleCreateFromTemplate = async () => {
    if (!projectId || !formTitle.trim() || !formTemplate.trim()) {
      setCreateError("Meeting title and template key are required.");
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      await createProjectMeeting(projectId, {
        title: formTitle.trim(),
        templateKey: formTemplate.trim(),
        status: PRISMA_EXECUTION_STATUS.planned,
      });
      const updated = await fetchProjectMeetings(projectId);
      setMeetings(updated);
      setSelectedMeetingId(updated[updated.length - 1]?.id ?? null);
      setFormTitle("");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create meeting";
      setCreateError(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleGenerateQuestions = async () => {
    if (!projectId || !formTemplate.trim()) {
      setCreateError("Template key is required to generate questions.");
      return;
    }

    setIsGenerating(true);
    setCreateError(null);

    try {
      const questions = await generateMeetingQuestions(projectId, {
        meetingTitle: formTitle.trim() || undefined,
        templateKey: formTemplate.trim(),
      });

      setGeneratedQuestions(questions);
      setIsReviewOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to generate questions";
      setCreateError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleConfirmGenerated = async (questions: ReviewedMeetingQuestion[]) => {
    if (!projectId || !formTemplate.trim() || !formTitle.trim()) {
      setCreateError("Meeting title and template key are required to create the meeting.");
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      await createProjectMeeting(projectId, {
        title: formTitle.trim(),
        templateKey: formTemplate.trim(),
        status: PRISMA_EXECUTION_STATUS.planned,
        questionsAndAnswers: questions.map(formatQuestion),
      });

      const updated = await fetchProjectMeetings(projectId);
      setMeetings(updated);
      setSelectedMeetingId(updated[updated.length - 1]?.id ?? null);
      setFormTitle("");
      setIsReviewOpen(false);
      setGeneratedQuestions([]);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create meeting";
      setCreateError(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateStatus = async (meetingId: string) => {
    const nextStatus = statusUpdates[meetingId];
    if (!nextStatus || !projectId) return;

    try {
      const updated = await updateProjectMeeting(projectId, meetingId, { status: nextStatus });
      setMeetings((current) => current.map((item) => (item.id === meetingId ? updated : item)));
      setStatusUpdates((current) => ({ ...current, [meetingId]: updated.status }));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update meeting";
      setError(message);
    }
  };

  const handleSelectTemplate = (value: string) => {
    setFormTemplate(value);
  };

  const meetingQuestions = editedQuestions;
  const artifactsForSelectedMeeting = selectedMeeting ? artifactsByMeeting[selectedMeeting.id] : undefined;
  const hasRequirementArtifacts = (artifactsForSelectedMeeting?.requirements?.length ?? 0) > 0;
  const hasSpecSectionArtifacts = (artifactsForSelectedMeeting?.specSections?.length ?? 0) > 0;
  const hasAnyDrafts = hasRequirementArtifacts || hasSpecSectionArtifacts;
  const hasAnsweredQuestions = meetingQuestions.some((question) => Boolean(question.answer && question.answer.trim()));
  const isLoadingArtifacts = artifactsLoadingMeetingId === selectedMeeting?.id;
  const isLoadingAudioAnalysis = audioAnalysisLoadingMeetingId === selectedMeeting?.id;
  const coachAudioSession =
    audioSession && audioSession.meetingId === selectedMeeting?.id
      ? audioSession
      : selectedMeetingAudioAnalysis?.session ?? null;
  const coachElapsedMs = isRecordingAudio
    ? recordingElapsedMs
    : selectedMeetingAudioAnalysis?.analysis.estimatedDurationMs ?? recordingElapsedMs;

  const handleRefreshMeetingCoach = async () => {
    if (!selectedMeeting) return;
    const liveSession =
      audioSessionRef.current && audioSessionRef.current.meetingId === selectedMeeting.id
        ? audioSessionRef.current
        : null;

    if (liveSession?.chunkCount && liveSession.status !== "completed") {
      await loadLiveMeetingAudioAnalysis(selectedMeeting.id, liveSession.id);
      return;
    }

    await loadLatestMeetingAudioAnalysis(selectedMeeting.id);
  };

  const handleOpenAudioImportPicker = () => {
    audioUploadInputRef.current?.click();
  };

  const handleAnswerChange = (id: string | undefined, index: number, value: string) => {
    setEditedQuestions((current) =>
      current.map((question, idx) => {
        const matchesId = id && question.id === id;
        const matchesIndex = !id && idx === index;

        if (matchesId || matchesIndex) {
          return { ...question, answer: value };
        }

        return question;
      }),
    );
  };

  const handleSaveAnswers = async () => {
    if (!selectedMeeting) return;

    setSaveAnswersLoading(true);
    setSaveAnswersError(null);

    try {
      const updated = await updateProjectMeetingQuestions(projectId, selectedMeeting.id, editedQuestions);
      setMeetings((current) => current.map((meeting) => (meeting.id === updated.id ? updated : meeting)));
      setSelectedMeetingId(updated.id);
      setEditedQuestions(updated.questionsAndAnswers ?? []);

      const allAnswered = (updated.questionsAndAnswers ?? []).every(
        (question) => Boolean(question.answer && question.answer.trim()),
      );

      setStatusUpdates((current) => ({
        ...current,
        [updated.id]: allAnswered
          ? EXECUTION_STATUS.done
          : normalizeMeetingStatus(updated.status),
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save answers";
      setSaveAnswersError(message);
    } finally {
      setSaveAnswersLoading(false);
    }
  };

  const handleGenerateArtifacts = async () => {
    if (!selectedMeeting) return;

    const answered = editedQuestions.filter((question) => Boolean(question.answer?.trim()));

    if (answered.length === 0) {
      setArtifactsError("Add at least one answer before generating drafts.");
      return;
    }

    setArtifactsLoadingMeetingId(selectedMeeting.id);
    setArtifactsError(null);

    try {
      await generateMeetingArtifacts(projectId, selectedMeeting.id);
      await loadArtifactsForMeeting(selectedMeeting.id, { updateLoadingState: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to generate drafts";
      setArtifactsError(message);
    } finally {
      setArtifactsLoadingMeetingId(null);
    }
  };

  const uploadAudioChunkBatchToSession = useCallback(
    async (
      session: MeetingAudioSession,
      chunks: { blob: Blob; filename: string }[],
      meetingId: string,
    ): Promise<MeetingAudioSession> => {
      if (!chunks.length) {
        throw new Error("No audio data to process.");
      }

      let uploadedSession = session;
      for (let index = 0; index < chunks.length; index += 1) {
        const chunk = chunks[index];
        if (!chunk.blob || chunk.blob.size === 0) {
          throw new Error(`Audio chunk ${index + 1} is empty.`);
        }
        setAudioAssistantStatus(`Upload du fichier ${index + 1}/${chunks.length}...`);
        uploadedSession = await uploadMeetingAudioChunk(projectId, meetingId, session.id, chunk.blob, {
          sequence: index,
          filename: chunk.filename,
        });
        audioSessionRef.current = uploadedSession;
        setAudioSession(uploadedSession);
      }

      return uploadedSession;
    },
    [setAudioAssistantStatus, projectId],
  );

  const finalizeAndApplyAudioSession = useCallback(
    async (meetingId: string, session: MeetingAudioSession): Promise<void> => {
      setAudioAssistantStatus("Finalisation et transcription...");
      keepAudioAssistantHelpful("Finalisation et transcription en cours...");

      const finalized = await finalizeMeetingAudioSession(projectId, meetingId, session.id, {
        applyToMeeting: true,
        transcription: {
          diarize: true,
        },
      });
      audioSessionRef.current = finalized.session;
      setAudioSession(finalized.session);
      setAudioAnalysisByMeeting((current) => ({
        ...current,
        [meetingId]: finalized,
      }));
      setAudioAnalysisError(null);
      setAudioTranscriptPreview(finalized.transcript.script?.trim() || finalized.transcript.text);
      await refreshSelectedMeeting(meetingId);
      setAudioAssistantStatus("Transcription terminée avec succès.", 6000);
    },
    [keepAudioAssistantHelpful, projectId, refreshSelectedMeeting, setAudioAssistantStatus],
  );

  const handleStartAudioRecording = async () => {
    if (!selectedMeeting) return;
    if (!supportsLiveRecording) {
      setAudioError("Live recording is not supported on this browser. Use file upload instead.");
      return;
    }

    setAudioError(null);
    setAudioAssistantStatus("Je prépare la session d’enregistrement...");
    setIsAudioBusy(true);

    try {
      resetAudioChunkUploadState();
      audioChunksRef.current = [];
      const session = await createMeetingAudioSession(projectId, selectedMeeting.id);
      audioSessionRef.current = session;
      setAudioSession(session);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredMimeType = getPreferredRecorderMimeType();
      const recorder = preferredMimeType
        ? new MediaRecorder(stream, { mimeType: preferredMimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      mediaStreamRef.current = stream;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onerror = () => {
        setAudioError("Recording failed. Stop and retry, or upload an audio file.");
      };

      recorder.start(15000);
      setRecordingStartedAtMs(Date.now());
      setRecordingElapsedMs(0);
      setAudioAssistantStatus("Recording in progress. The final diarized analysis will start when you stop.");
      setIsRecordingAudio(true);
    } catch (err) {
      releaseRecorderResources();
      setIsRecordingAudio(false);
      setRecordingStartedAtMs(null);
      const message = err instanceof Error ? err.message : "Failed to start audio recording";
      setAudioError(message);
    } finally {
      setIsAudioBusy(false);
    }
  };

  const handleStopAudioRecording = async () => {
    if (!selectedMeeting || !audioSession) return;
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      setAudioError("No active recording session to stop.");
      return;
    }

    setAudioError(null);
    setIsAudioBusy(true);
    const recordedMimeType = recorder.mimeType || audioSession.mimeType || "audio/webm";

    try {
      if (recorder.state !== "inactive") {
        await new Promise<void>((resolve, reject) => {
          recorder.addEventListener("stop", () => resolve(), { once: true });
          recorder.addEventListener("error", () => reject(new Error("Recorder stopped with error")), {
            once: true,
          });
          recorder.stop();
        });
      }
      releaseRecorderResources();
      setIsRecordingAudio(false);
      setRecordingStartedAtMs(null);
      setAudioAssistantStatus("Uploading recording and finalizing...");

      const audioBlob = new Blob(audioChunksRef.current, { type: recordedMimeType });
      audioChunksRef.current = [];
      if (audioBlob.size <= 0) {
        throw new Error("No audio captured. Please try again.");
      }

      const latestSession = audioSessionRef.current ?? audioSession;
      if (!latestSession) {
        throw new Error("No active audio session found.");
      }

      const filename = buildAudioUploadFilename(
        selectedMeeting.id,
        { name: `meeting-${selectedMeeting.id}-recording-${Date.now()}`, type: recordedMimeType },
        0,
      );
      const uploaded = await uploadAudioChunkBatchToSession(
        latestSession,
        [{ blob: audioBlob, filename }],
        selectedMeeting.id,
      );

      await finalizeAndApplyAudioSession(selectedMeeting.id, uploaded);
    } catch (err) {
      releaseRecorderResources();
      setIsRecordingAudio(false);
      setRecordingStartedAtMs(null);
      audioChunksRef.current = [];
      const message = err instanceof Error ? err.message : "Failed to process recorded audio";
      setAudioError(message);
      setAudioAssistantStatus(message);
    } finally {
      setIsAudioBusy(false);
    }
  };

  const handleAudioFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const selectedFiles = files.filter(Boolean);
    event.target.value = "";
    if (!selectedMeeting || selectedFiles.length === 0) {
      return;
    }

    setAudioError(null);
    setIsAudioBusy(true);
    setAudioAssistantStatus("Création de la session audio...");

    try {
      const session = await createMeetingAudioSession(projectId, selectedMeeting.id);
      audioSessionRef.current = session;
      setAudioSession(session);

      const chunks = selectedFiles.map((file, index) => ({
        blob: file,
        filename: buildAudioUploadFilename(selectedMeeting.id, file, index),
      }));
      const uploaded = await uploadAudioChunkBatchToSession(session, chunks, selectedMeeting.id);
      await finalizeAndApplyAudioSession(selectedMeeting.id, uploaded);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to upload audio file";
      setAudioError(message);
      setAudioAssistantStatus(message);
    } finally {
      setIsAudioBusy(false);
    }
  };

  const handleRefreshAudioSession = async () => {
    if (!selectedMeeting || !audioSession) return;
    setAudioError(null);
    setIsAudioBusy(true);
    setAudioAssistantStatus("Chargement de l’état de session...");

    try {
      const session = await getMeetingAudioSession(projectId, selectedMeeting.id, audioSession.id);
      audioSessionRef.current = session;
      setAudioSession(session);
      setAudioAssistantStatus("Session rechargée.");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to refresh audio session";
      setAudioError(message);
      setAudioAssistantStatus(message);
    } finally {
      setIsAudioBusy(false);
    }
  };

  const handleDeleteMeeting = async (meeting: ProjectMeeting, event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this meeting? This cannot be undone.")) return;

    try {
      await deleteProjectMeeting(projectId, meeting.id);
      setMeetings((current) => current.filter((item) => item.id !== meeting.id));
      setStatusUpdates((current) => {
        const next = { ...current };
        delete next[meeting.id];
        return next;
      });
      setAudioAnalysisByMeeting((current) => {
        const next = { ...current };
        delete next[meeting.id];
        return next;
      });

      if (selectedMeetingId === meeting.id) {
        releaseRecorderResources();
        setIsRecordingAudio(false);
        setRecordingStartedAtMs(null);
        setRecordingElapsedMs(0);
        audioSessionRef.current = null;
        setAudioSession(null);
        setAudioTranscriptPreview("");
        setSelectedMeetingId(null);
        setEditedQuestions([]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete meeting";
      setError(message);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {showHeader ? <PageHeader title="Meetings & discovery" /> : null}

      {error ? <InlineAlert tone="danger" title={error} /> : null}

      <Card title="Plan a meeting">
        <div className="flex flex-col gap-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-700">
              <span className="text-xs font-semibold uppercase text-slate-500">Meeting title</span>
              <Input
                type="text"
                value={formTitle}
                onChange={(event) => setFormTitle(event.target.value)}
                placeholder="Discovery workshop, stakeholder sync..."
              />
            </label>

            <label className="flex flex-col gap-1 text-sm text-slate-700">
              <span className="text-xs font-semibold uppercase text-slate-500">Template</span>
              <div className="flex gap-2">
                <Select
                  className="flex-1"
                  value={formTemplate}
                  onChange={(event) => handleSelectTemplate(event.target.value)}
                >
                  {RECOMMENDED_TEMPLATES.map((template) => (
                    <option key={template.key} value={template.key}>
                      {template.label}
                    </option>
                  ))}
                </Select>
                <Input
                  className="flex-1"
                  type="text"
                  value={formTemplate}
                  onChange={(event) => handleSelectTemplate(event.target.value)}
                  placeholder="custom template key"
                />
              </div>
            </label>
          </div>

          {createError ? <InlineAlert tone="danger" title={createError} /> : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="secondary" onClick={handleCreateFromTemplate} disabled={isCreating}>
              {isCreating ? "Creating..." : "Create from template"}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleGenerateQuestions}
              disabled={isGenerating || isCreating}
            >
              {isGenerating ? "Generating questions..." : "Generate with AI"}
            </Button>
            <div className="text-xs text-slate-500">
              Use AI to draft discovery prompts, then curate them before saving the meeting.
            </div>
          </div>
        </div>
      </Card>

      <Card title="Past and scheduled meetings">
        {isLoading ? (
          <div className="p-4 text-sm text-slate-700">Loading meetings...</div>
        ) : meetings.length === 0 ? (
          <EmptyState
            title="No meetings yet"
            description="Start with a template or generate questions to create your first discovery session."
          />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="overflow-auto">
              <Table dense className="min-w-full text-slate-800">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Title</TableHeaderCell>
                    <TableHeaderCell>Template</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Questions</TableHeaderCell>
                    <TableHeaderCell>Updated</TableHeaderCell>
                    <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meetings.map((meeting) => (
                    <TableRow
                      key={meeting.id}
                      className={`cursor-pointer hover:bg-slate-50 ${selectedMeetingId === meeting.id ? "bg-slate-50" : ""}`}
                      onClick={() => setSelectedMeetingId(meeting.id)}
                    >
                      <TableCell className="font-semibold text-slate-900">{meeting.title}</TableCell>
                      <TableCell className="text-slate-700">{meeting.templateKey}</TableCell>
                      <TableCell className="text-slate-700">{renderStatusBadge(meeting.status)}</TableCell>
                      <TableCell className="text-slate-700">{meeting.questionsAndAnswers?.length ?? 0}</TableCell>
                      <TableCell className="text-slate-700">{formatDate(meeting.updatedAt)}</TableCell>
                      <TableCell className="text-right text-slate-700">
                        <div className="flex items-center justify-end gap-2">
                          <Select
                            className="max-w-[140px]"
                            value={statusUpdates[meeting.id] ?? meeting.status}
                            onChange={(event) =>
                              setStatusUpdates((current) => ({
                                ...current,
                                [meeting.id]: event.target.value as ProjectMeetingStatus,
                              }))
                            }
                          >
                            {Object.keys(STATUS_BADGES).map((status) => (
                              <option key={status} value={status}>
                                {STATUS_BADGES[status as ProjectMeetingStatus]?.label ?? status}
                              </option>
                            ))}
                          </Select>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleUpdateStatus(meeting.id);
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            type="button"
                            variant="link"
                            className="text-rose-600"
                            onClick={(event) => handleDeleteMeeting(meeting, event)}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {selectedMeeting ? (
              <div className="rounded border border-slate-200 p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500">Selected meeting</div>
                    <div className="text-lg font-semibold text-slate-900">{selectedMeeting.title}</div>
                    <div className="text-sm text-slate-600">Template: {selectedMeeting.templateKey}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-sm text-slate-600">
                    {renderStatusBadge(selectedMeeting.status)}
                    <div>Created {formatDate(selectedMeeting.createdAt)}</div>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="text-xs font-semibold uppercase text-slate-500">Mobile audio capture</div>
                  <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="flex flex-col gap-3">
                      <div className="text-sm text-slate-700">
                        Record from phone or upload audio files, then auto-generate transcript into this meeting.
                      </div>

                      {shouldPreferNativeRecorder ? (
                        <div className="rounded border border-amber-200 bg-amber-50 p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge label={isIOSDevice ? "iPhone / iPad" : "Mobile"} variant="default" />
                            {isStandaloneWebApp ? <Badge label="PWA" variant="default" /> : null}
                            <Badge label="Recommended" variant="success" />
                          </div>
                          <div className="mt-2 text-sm text-amber-950">
                            Use the native recorder for long meetings. Record in Voice Memos, place the phone near the
                            center of the table, then import the `.m4a` or `.wav` file here.
                          </div>
                          <div className="mt-3 grid gap-2 md:grid-cols-3">
                            <div className="rounded border border-amber-200 bg-white p-2 text-sm text-slate-700">
                              1. Open Voice Memos and keep the phone stable in the middle of the discussion.
                            </div>
                            <div className="rounded border border-amber-200 bg-white p-2 text-sm text-slate-700">
                              2. Let the screen sleep if needed while the native recorder runs.
                            </div>
                            <div className="rounded border border-amber-200 bg-white p-2 text-sm text-slate-700">
                              3. Come back to Forum only when the meeting ends, then import the audio file.
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <Button
                              type="button"
                              variant="primary"
                              onClick={handleOpenAudioImportPicker}
                              disabled={isAudioBusy || isRecordingAudio}
                            >
                              Import Voice Memo
                            </Button>
                            {shouldWarnAboutBackgroundCapture ? (
                              <div className="text-xs text-amber-900">
                                Browser recording in iPhone PWA mode is foreground-only and may pause if the screen
                                locks.
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}

                      {audioAssistantMessage ? (
                        <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                          {audioAssistantMessage}
                        </div>
                      ) : null}

                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        <Button
                          type="button"
                          variant={isRecordingAudio ? "secondary" : shouldPreferNativeRecorder ? "secondary" : "primary"}
                          className="w-full justify-center"
                          onClick={isRecordingAudio ? handleStopAudioRecording : handleStartAudioRecording}
                          disabled={isAudioBusy || (!isRecordingAudio && !supportsLiveRecording)}
                        >
                          {isRecordingAudio
                            ? isAudioBusy
                              ? "Stopping and transcribing..."
                              : "Stop and transcribe"
                            : isAudioBusy
                              ? "Starting..."
                              : shouldPreferNativeRecorder
                                ? "Record in browser"
                                : "Record with microphone"}
                        </Button>

                        <Button
                          type="button"
                          variant="secondary"
                          className="w-full justify-center"
                          onClick={handleRefreshAudioSession}
                          disabled={isAudioBusy || !audioSession}
                        >
                          Refresh session
                        </Button>
                      </div>

                      <label className="flex flex-col gap-1 text-sm text-slate-700">
                        <span className="text-xs font-semibold uppercase text-slate-500">
                          {shouldPreferNativeRecorder
                            ? "Recommended import: Voice Memos or recorded file(s)"
                            : "Fallback: upload recorded file(s)"}
                        </span>
                        <Input
                          ref={audioUploadInputRef}
                          type="file"
                          accept={AUDIO_UPLOAD_ACCEPT}
                          multiple
                          capture={shouldPreferNativeRecorder ? undefined : "user"}
                          className="w-full"
                          onChange={handleAudioFileUpload}
                          disabled={isAudioBusy || isRecordingAudio}
                        />
                      </label>

                      {!supportsLiveRecording ? (
                        <div className="text-xs text-amber-700">
                          Live recording is not available on this browser. Use file upload on mobile.
                        </div>
                      ) : null}

                      {shouldWarnAboutBackgroundCapture ? (
                        <InlineAlert
                          tone="warning"
                          title="iPhone PWA limitation"
                          description="If the screen locks or the app goes to the background, browser microphone capture may pause. Native Voice Memos import is recommended for meeting recordings."
                        />
                      ) : null}

                      {audioSession ? (
                        <div className="text-xs text-slate-600">
                          Session {audioSession.id} - status: {audioSession.status}, chunks: {audioSession.chunkCount},
                          bytes: {audioSession.totalBytes}
                        </div>
                      ) : null}

                      {audioError ? <InlineAlert tone="danger" title={audioError} description={audioRecoveryHint ?? undefined} /> : null}

                      <div>
                        <div className="text-xs font-semibold uppercase text-slate-500">Transcript preview</div>
                        {audioTranscriptPreview ? (
                          <div className="mt-1 rounded border border-slate-200 bg-white p-2 text-sm text-slate-800 whitespace-pre-wrap">
                            {audioTranscriptPreview}
                          </div>
                        ) : (
                          <div className="mt-1 text-sm text-slate-600">
                            No transcript yet for this meeting.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <MeetingCoachPanel
                  analysis={selectedMeetingAudioAnalysis?.analysis ?? null}
                  audioSession={coachAudioSession}
                  isRecording={isRecordingAudio}
                  recordingElapsedMs={coachElapsedMs}
                  templateKey={selectedMeeting.templateKey}
                  error={audioAnalysisError}
                  isLoading={isLoadingAudioAnalysis}
                  onRefresh={() => {
                    void handleRefreshMeetingCoach();
                  }}
                />

                <div className="mt-4 space-y-3">
                  <div className="text-xs font-semibold uppercase text-slate-500">Questions</div>
                  {meetingQuestions.length === 0 ? (
                    <div className="rounded border border-dashed border-slate-200 p-3 text-sm text-slate-700">
                      No questions captured yet for this meeting.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {meetingQuestions.map((question, index) => (
                        <div key={question.id ?? `${question.question}-${index}`} className="mb-4">
                          <div className="text-sm text-gray-500">
                            {index + 1}. {question.category || "Uncategorized"} – {question.intent || "No intent"}
                          </div>
                          <div className="font-medium mb-1">{question.question}</div>
                          <Textarea
                            rows={3}
                            value={question.answer ?? ""}
                            onChange={(e) => handleAnswerChange(question.id, index, e.target.value)}
                            placeholder="Type the client’s answer or meeting notes here…"
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            {question.answer && question.answer.trim().length > 0 ? "Answered" : "Pending"}
                          </div>
                        </div>
                      ))}
                      <div className="flex flex-col gap-2">
                        {saveAnswersError ? (
                          <InlineAlert tone="danger" title={saveAnswersError} />
                        ) : null}
                        <Button
                          type="button"
                          variant="primary"
                          disabled={!selectedMeeting || saveAnswersLoading}
                          onClick={handleSaveAnswers}
                        >
                          {saveAnswersLoading ? "Saving…" : "Save answers"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 space-y-3">
                  <div className="text-xs font-semibold uppercase text-slate-500">AI-generated drafts</div>
                  {!hasAnsweredQuestions ? (
                    <div className="rounded border border-dashed border-slate-200 p-3 text-sm text-slate-700">
                      Add meeting answers first, then generate draft requirements and spec sections.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <Button
                          type="button"
                          variant="primary"
                          onClick={handleGenerateArtifacts}
                          disabled={isLoadingArtifacts}
                        >
                          {isLoadingArtifacts
                            ? "Refreshing drafts..."
                            : artifactsForSelectedMeeting
                              ? "Regenerate drafts"
                              : "Generate drafts"}
                        </Button>
                        <div className="text-xs text-slate-500">
                          Turn answered questions into requirement and spec section drafts.
                        </div>
                      </div>

                      {artifactsError ? <InlineAlert tone="danger" title={artifactsError} /> : null}

                      {isLoadingArtifacts ? (
                        <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                          Loading drafts for this meeting...
                        </div>
                      ) : !artifactsForSelectedMeeting ? (
                        <div className="rounded border border-dashed border-slate-200 p-3 text-sm text-slate-700">
                          Generate drafts to review AI suggestions before creating requirements or specs.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {!hasAnyDrafts ? (
                            <div className="rounded border border-dashed border-slate-200 p-3 text-sm text-slate-700">
                              No drafts generated. Add answers to meeting questions before generating drafts.
                            </div>
                          ) : null}

                          <div className="grid gap-4 lg:grid-cols-2">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-semibold text-slate-800">
                                Requirements ({artifactsForSelectedMeeting.requirements?.length ?? 0})
                              </div>
                              <div className="text-xs text-slate-500">
                                Saved to your project as meeting AI artifacts.
                              </div>
                            </div>

                            {(artifactsForSelectedMeeting.requirements ?? []).length === 0 ? (
                              <div className="rounded border border-slate-200 p-3 text-sm text-slate-700">
                                No requirement drafts yet. Generate again to populate suggestions.
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {artifactsForSelectedMeeting.requirements?.map((requirement, index) => (
                                  <div
                                    key={requirement.id ?? `${requirement.title}-${index}`}
                                    className="rounded border border-slate-200 p-3 text-sm text-slate-800"
                                  >
                                    <div className="mb-2 flex items-center justify-between gap-2 text-xs text-slate-500">
                                      <span>Requirement {index + 1}</span>
                                      <Link
                                        className="button button-secondary button-sm"
                                        to={`/projects/${projectId}?tab=requirements&requirementId=${encodeURIComponent(requirement.id)}`}
                                      >
                                        View requirement
                                      </Link>
                                    </div>

                                    <div className="space-y-2 text-xs text-slate-600">
                                      <div>
                                        <span className="font-semibold uppercase text-slate-500">Title</span>
                                        <div className="text-sm font-semibold text-slate-800">
                                          {requirement.title || "Untitled requirement"}
                                        </div>
                                      </div>

                                      <div>
                                        <span className="font-semibold uppercase text-slate-500">Description</span>
                                        <div className="rounded border border-slate-200 bg-slate-50 p-2 text-sm text-slate-800 whitespace-pre-wrap">
                                          {requirement.description || "No description"}
                                        </div>
                                      </div>

                                      {requirement.generatedSummary ? (
                                        <div>
                                          <span className="font-semibold uppercase text-slate-500">Generated summary</span>
                                          <div className="rounded border border-dashed border-slate-200 p-2 text-sm text-slate-700 whitespace-pre-wrap">
                                            {requirement.generatedSummary}
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-semibold text-slate-800">
                                Spec sections ({artifactsForSelectedMeeting.specSections?.length ?? 0})
                              </div>
                              <div className="text-xs text-slate-500">Persisted alongside other spec sections.</div>
                            </div>

                            {(artifactsForSelectedMeeting.specSections ?? []).length === 0 ? (
                              <div className="rounded border border-slate-200 p-3 text-sm text-slate-700">
                                No spec section drafts yet. Generate again to populate suggestions.
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {artifactsForSelectedMeeting.specSections?.map((section, index) => (
                                  <div
                                    key={section.id ?? `${section.title}-${index}`}
                                    className="rounded border border-slate-200 p-3 text-sm text-slate-800"
                                  >
                                    <div className="mb-2 flex items-center justify-between gap-2 text-xs text-slate-500">
                                      <span>Spec section {index + 1}</span>
                                      <Link
                                        className="button button-secondary button-sm"
                                        to={`/projects/${projectId}?tab=specs&specSectionId=${encodeURIComponent(section.id)}`}
                                      >
                                        View spec section
                                      </Link>
                                    </div>

                                    <div className="space-y-2 text-xs text-slate-600">
                                      <div>
                                        <span className="font-semibold uppercase text-slate-500">Title</span>
                                        <div className="text-sm font-semibold text-slate-800">
                                          {section.title || "Untitled section"}
                                        </div>
                                      </div>

                                      {section.summary ? (
                                        <div>
                                          <span className="font-semibold uppercase text-slate-500">Summary</span>
                                          <div className="rounded border border-slate-200 bg-slate-50 p-2 text-sm text-slate-800 whitespace-pre-wrap">
                                            {section.summary}
                                          </div>
                                        </div>
                                      ) : null}

                                      <div>
                                        <span className="font-semibold uppercase text-slate-500">Content</span>
                                        <div className="rounded border border-slate-200 bg-slate-50 p-2 text-sm text-slate-800 whitespace-pre-wrap">
                                          {section.content || "No content"}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </Card>

      <ReviewGeneratedMeetingQuestionsModal
        isOpen={isReviewOpen}
        meetingTitle={formTitle}
        questions={generatedQuestions}
        isSubmitting={isCreating}
        onCancel={() => setIsReviewOpen(false)}
        onConfirm={handleConfirmGenerated}
      />
    </div>
  );
}
